// src/blogPosts.jsx
// Blog post data. Content is a React component — rendered inside BlogPost.jsx's .prose wrapper.

const Post1Content = () => (
  <>
    <p>
      After every medical appointment, procedure, or hospital stay, your insurance company sends
      you a document called an Explanation of Benefits — an EOB. Most people glance at it, assume
      it's just a receipt, and throw it away. That's a mistake that can cost you real money.
    </p>
    <p>
      An EOB is not a bill. But it tells you everything you need to know to verify your bill is
      correct — and studies consistently show that <strong>up to 80% of medical bills contain at
      least one error</strong>. The EOB is your best tool for catching them.
    </p>

    <h2>The sections of an EOB</h2>
    <p>Every EOB is formatted differently depending on your insurer, but they all contain the same core information:</p>
    <ul>
      <li><strong>Dates of service</strong> — the dates your care was provided. Verify these match your actual visits. Duplicate dates are a common error.</li>
      <li><strong>Provider information</strong> — the name of the doctor, hospital, or facility. Make sure you recognize them.</li>
      <li><strong>Procedure codes (CPT codes)</strong> — these are the five-digit codes that describe exactly what service was performed. One wrong digit can change what you owe by hundreds of dollars.</li>
      <li><strong>Amount billed</strong> — what the provider charged. This number is often dramatically higher than what you'll actually owe.</li>
      <li><strong>Allowed amount (or negotiated rate)</strong> — what your insurance company has agreed to pay for that service. If your provider is in-network, this is the most you should owe — not the billed amount.</li>
      <li><strong>Plan paid</strong> — what your insurance company is paying directly to the provider.</li>
      <li><strong>Your responsibility</strong> — what you actually owe after insurance pays their share. This is the number that matters.</li>
    </ul>

    <h2>What to look for</h2>
    <p>Run through these checks every time you receive an EOB:</p>
    <ul>
      <li><strong>Do the dates match?</strong> You shouldn't see a date when you weren't seen. Duplicate billing (the same service billed twice) is one of the most common errors.</li>
      <li><strong>Do the services match what you received?</strong> If your EOB says you had a procedure you didn't have, that's upcoding — billing for a more expensive service than what was actually provided.</li>
      <li><strong>Are there services listed as "not covered" that should be?</strong> Sometimes coverage denials are the result of a coding error, not an actual policy exclusion. A code submitted under the wrong diagnosis can trigger a denial.</li>
      <li><strong>Does the "your responsibility" column match your actual bill?</strong> The number on your EOB and the number on the bill from your provider should match. If they don't, call your provider before paying anything.</li>
    </ul>

    <h2>What to do when you find an error</h2>
    <p>
      Don't panic — and don't pay. Call your insurance company using the number on your EOB and
      describe the discrepancy. Get the name of the representative you speak with and write it down,
      along with the date and what they told you. Then call your provider's billing department and
      let them know there's a discrepancy that needs to be corrected.
    </p>
    <p>
      If your insurance company denies a claim you think should be covered, you have the right to
      appeal. The denial letter will explain the reason for the denial and the deadline to appeal —
      don't miss that deadline.
    </p>
    <p>
      You shouldn't need to know all of this going in. That's what Irene is here for.
    </p>
  </>
);

const Post2Content = () => (
  <>
    <p>
      If you've received a large hospital bill and the amount feels impossible, there's something
      important you should know before you do anything else: <strong>most nonprofit hospitals in the
      United States are legally required to offer financial assistance programs</strong> — and most
      families who qualify never apply, simply because no one told them these programs exist.
    </p>
    <p>
      These programs go by different names: charity care, financial assistance, sliding-scale fees.
      But they all mean the same thing: a significant reduction — sometimes 100% — in what you owe,
      based on your income and family size.
    </p>

    <h2>Why this is required by law</h2>
    <p>
      Under IRS Section 501(r), any nonprofit hospital that wants to maintain its tax-exempt status
      must offer financial assistance to patients who qualify. They're also required to publicize
      the program and make the application widely available. If a hospital can't demonstrate they've
      done this, they risk losing their tax exemption.
    </p>
    <p>
      This applies to the vast majority of hospitals in the U.S. — about 60% of community hospitals
      are nonprofit. For-profit hospitals are not required to offer these programs, but many do
      anyway, and it's always worth asking.
    </p>

    <h2>Who qualifies?</h2>
    <p>
      Eligibility thresholds vary by hospital, but many programs cover families earning up to 400%
      of the federal poverty level — which is <strong>around $120,000 for a family of four</strong>.
      Some hospitals go higher. A handful have eliminated patient bills entirely for families below
      certain income levels.
    </p>
    <p>
      Don't assume you don't qualify because you have insurance, own a home, or have savings. Many
      programs look only at income. And most hospitals have some form of assistance available at
      every income level — even if it's just a reduced interest payment plan rather than full
      forgiveness.
    </p>

    <h2>How to apply</h2>
    <ul>
      <li><strong>Ask before you pay anything.</strong> Applying for financial assistance pauses the billing process at most hospitals. Never make a large payment before finding out if you qualify for assistance.</li>
      <li><strong>Look for the financial assistance policy on the hospital's website.</strong> Under 501(r), hospitals must post their policy publicly. Search "[hospital name] financial assistance policy" or "charity care application."</li>
      <li><strong>Call the hospital billing department directly.</strong> Ask to speak with a financial counselor. Use those exact words — financial counselor, not billing representative. They have more flexibility.</li>
      <li><strong>Gather your documents.</strong> You'll typically need recent pay stubs, last year's tax return, and proof of any other income. Some hospitals only require a self-attestation of income.</li>
      <li><strong>Apply even if you're not sure you qualify.</strong> The worst they can say is no, and at many hospitals, the threshold is higher than families expect.</li>
    </ul>

    <h2>What happens after you apply</h2>
    <p>
      Processing times vary, but most hospitals respond within 30 days. If you're approved, your
      bill will be recalculated based on the assistance level you qualify for. Keep copies of
      everything you submit and everything they send you.
    </p>
    <p>
      If you're denied, ask why — and ask whether there are other assistance options. Many hospitals
      have multiple tiers of programs and payment plans that aren't automatically offered.
    </p>
    <p>
      Irene can help you find the specific financial assistance program for your hospital and walk
      you through the application process step by step.
    </p>
  </>
);

const Post3Content = () => (
  <>
    <p>
      In January 2022, a federal law called the No Surprises Act went into effect. It was passed
      specifically to address one of the most common and most infuriating experiences in American
      healthcare: getting a bill from a doctor you never chose and never knew was out-of-network.
    </p>
    <p>
      If you've ever been surprised by a bill from an anesthesiologist, an ER physician, or a
      radiologist after a procedure at an in-network facility — this law was written for situations
      exactly like yours.
    </p>

    <h2>What the law covers</h2>
    <p>The No Surprises Act protects you in two main situations:</p>
    <ul>
      <li>
        <strong>Emergency care at any facility.</strong> If you have a medical emergency and are
        taken to an out-of-network hospital, your insurer must cover the care as if it were
        in-network. You can only be billed your in-network cost-sharing amount (your deductible,
        copay, or coinsurance). This applies even if you're unconscious and had no choice.
      </li>
      <li>
        <strong>Out-of-network providers at in-network facilities.</strong> If you go to an
        in-network hospital or surgery center for a non-emergency procedure, but one of the
        providers involved — an anesthesiologist, assistant surgeon, or specialist — is
        out-of-network, you cannot be billed more than your in-network cost-sharing amount for
        their services. This is the "surprise billing" the law is most known for eliminating.
      </li>
    </ul>

    <h2>What it doesn't cover</h2>
    <p>
      The law has some important limitations. It does <strong>not</strong> apply if you
      voluntarily and knowingly chose to see an out-of-network provider when an in-network
      option was available — and signed a consent form acknowledging it. It also doesn't apply
      to ground ambulance services (though many states have their own protections for that).
    </p>
    <p>
      It applies to most private insurance plans, but <strong>not</strong> to Medicaid, Medicare,
      CHIP, or VA health programs, which have their own separate rules.
    </p>

    <h2>How to use it if you've received a surprise bill</h2>
    <ul>
      <li><strong>Compare the bill to your EOB.</strong> Your Explanation of Benefits from your insurance company should show whether the provider was in-network or out-of-network and what your insurer says you owe.</li>
      <li><strong>Contact your insurance company first.</strong> Tell them you received a bill that you believe violates the No Surprises Act. Ask them to review it. In many cases, the insurer will work with the provider directly to resolve it.</li>
      <li><strong>File a complaint if needed.</strong> If your insurer doesn't resolve it, you can file a complaint with the federal No Surprises Help Desk at 1-800-985-3059 or at cms.gov/nosurprises.</li>
      <li><strong>Don't pay while it's under review.</strong> Providers generally cannot send a surprise bill to collections while it's being disputed under the No Surprises Act.</li>
    </ul>

    <h2>The bottom line</h2>
    <p>
      The No Surprises Act doesn't solve every medical billing problem — but it's one of the most
      significant patient protections enacted in decades. If you've received a bill that looks like
      it might violate these rules, don't pay it without investigating first.
    </p>
    <p>
      Irene can help you figure out whether your bill falls under the No Surprises Act and what
      steps to take next.
    </p>
  </>
);

export const blogPosts = [
  {
    slug: "how-to-read-an-eob",
    title: "How to Read Your Explanation of Benefits (EOB) — and Why It Matters",
    date: "March 20, 2026",
    excerpt:
      "Your insurance company sends you an EOB after every medical service — but most people throw them away without reading them. That's a mistake. Here's what every line means and what errors to look for.",
    content: Post1Content,
  },
  {
    slug: "hospital-financial-assistance",
    title: "Hospital Financial Assistance: The Program Most Families Never Know About",
    date: "March 12, 2026",
    excerpt:
      "Most nonprofit hospitals are legally required to offer financial assistance programs — but they don't advertise them. Here's how to find out if you qualify and how to apply before paying a single dollar.",
    content: Post2Content,
  },
  {
    slug: "no-surprises-act",
    title: "The No Surprises Act: Your Protection Against Unexpected Medical Bills",
    date: "March 5, 2026",
    excerpt:
      "Since 2022, a federal law protects you from being billed by out-of-network providers in many situations. Here's what it covers, what it doesn't, and how to use it if you've already received a surprise bill.",
    content: Post3Content,
  },
];

export function getPostBySlug(slug) {
  return blogPosts.find((p) => p.slug === slug) || null;
}
